import React from "react";
import type { LucideIcon } from "lucide-react";
import SimpleTooltip from "./SimpleTooltip";

export interface StatCardConfig {
  key: string;
  label: string;
  color?: string;
  icon?: LucideIcon;
  iconBg?: string;
  iconColor?: string;
  suffix?: string;
  formatter?: (value: number) => string | number;
  description?: string;
}

interface StatsCardsProps {
  stats: Record<string, unknown> | null;
  cards: StatCardConfig[];
  loading?: boolean;
  gridCols?: string;
  variant?: "colored-icon" | "simple-icon";
  cardClassName?: string;
  containerClassName?: string;
  valueOnTop?: boolean;
}

const StatsCards: React.FC<StatsCardsProps> = ({
  stats,
  cards,
  loading = false,
  gridCols = "lg:grid-cols-4",
  variant = "colored-icon",
  cardClassName,
  containerClassName,
  valueOnTop = false,
}) => {
  const getValue = (key: string): string | number => {
    if (!stats) return 0;
    const value = stats[key];
    if (typeof value === "number") {
      if (key.toLowerCase().includes("rate")) {
        return Number(value.toFixed(1));
      }
      return value;
    }
    return (value ?? 0) as number;
  };

  const getDisplayValue = (card: StatCardConfig, value: string | number): string | number => {
    if (card.formatter && typeof value === "number") {
      return card.formatter(value);
    }
    return value;
  };

  if (variant === "simple-icon") {
    return (
      <div className={containerClassName || `grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4 sm:gap-5`}>
        {cards.map((card) => {
          const Icon = card.icon;
          const value = getValue(card.key);
          const displayValue = getDisplayValue(card, value);
          const fullValueText = `${displayValue}${card.suffix || ""}`;

          return (
            <div
              key={card.key}
              className={`flex items-center gap-3 sm:gap-4 min-w-0 ${
                cardClassName ||
                "bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-[18px] sm:p-6 shadow-soft min-w-0 transition-all duration-150"
              }`}
            >
              {Icon && (
                <div
                  className={`w-11 h-11 rounded-[12px] flex items-center justify-center flex-none ${card.color || ""}`}
                  style={card.color ? undefined : { background: card.iconBg || "var(--color-primary-100)" }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={card.color ? undefined : { color: card.iconColor || "var(--color-primary-600)" }}
                  />
                </div>
              )}
              {valueOnTop ? (
                <div className="flex-grow min-w-0">
                  <div className="text-[22px] sm:text-[24px] font-extrabold leading-none text-[var(--color-text-primary)] mb-1 truncate">
                    {loading ? (
                      <div className="h-6 w-12 bg-[var(--color-bg-skeleton)] rounded animate-pulse" />
                    ) : (
                      <SimpleTooltip content={fullValueText} className="min-w-0 w-full block cursor-default">
                        <span className="truncate block">
                          {displayValue}
                          {card.suffix && (
                            <span className="text-sm sm:text-base text-[var(--color-text-tertiary)] ml-0.5">
                              {card.suffix}
                            </span>
                          )}
                        </span>
                      </SimpleTooltip>
                    )}
                  </div>
                  <SimpleTooltip content={card.label} className="min-w-0 w-full block cursor-default">
                    <div className="text-xs font-bold leading-tight text-[var(--color-text-primary)] truncate">
                      {card.label}
                    </div>
                  </SimpleTooltip>
                </div>
              ) : (
                <div className="min-w-0 flex-grow">
                  <SimpleTooltip content={card.label} className="min-w-0 w-full block cursor-default">
                    <div className="text-[12px] font-semibold text-[var(--color-text-tertiary)] truncate">
                      {card.label}
                    </div>
                  </SimpleTooltip>
                  <div className="text-[22px] font-extrabold leading-none text-[var(--color-text-primary)] tracking-tight truncate">
                    {loading ? (
                      <div className="h-6 w-12 bg-[var(--color-bg-skeleton)] rounded animate-pulse" />
                    ) : (
                      <SimpleTooltip content={fullValueText} className="min-w-0 w-full block cursor-default">
                        <span className="truncate block">{displayValue}</span>
                      </SimpleTooltip>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={containerClassName || `grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4 sm:gap-5`}>
      {cards.map((card) => {
        const Icon = card.icon;
        const value = getValue(card.key);
        const displayValue = getDisplayValue(card, value);
        const fullValueText = `${displayValue}${card.suffix || ""}`;

        return (
          <div
            key={card.key}
            className={
              cardClassName ||
              "bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-[18px] sm:p-6 shadow-soft min-w-0 transition-all duration-150"
            }
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between h-full">
              <div className="flex-1 min-w-0 mb-3 sm:mb-0 pr-2">
                <SimpleTooltip content={card.label} className="min-w-0 w-full block cursor-default">
                  <p className="text-[12px] sm:text-[12.5px] font-semibold text-[var(--color-text-tertiary)] mb-1 sm:mb-2 leading-tight truncate">
                    {card.label}
                  </p>
                </SimpleTooltip>
                {loading ? (
                  <div className="h-8 sm:h-10 lg:h-12 w-16 bg-[var(--color-bg-skeleton)] rounded animate-pulse" />
                ) : (
                  <SimpleTooltip content={fullValueText} className="min-w-0 w-full block cursor-default">
                    <p className="text-2xl sm:text-3xl lg:text-[28px] font-extrabold text-[var(--color-text-primary)] mb-2 tracking-tight truncate">
                      {displayValue}
                      {card.suffix && (
                        <span className="text-lg sm:text-xl lg:text-2xl text-[var(--color-text-tertiary)] ml-0.5">
                          {card.suffix}
                        </span>
                      )}
                    </p>
                  </SimpleTooltip>
                )}
                {card.description && (
                  <SimpleTooltip content={card.description} className="min-w-0 w-full block cursor-default">
                    <p className="text-[11.5px] text-[var(--color-text-tertiary)] mt-1 font-medium truncate">
                      {card.description}
                    </p>
                  </SimpleTooltip>
                )}
              </div>
              {Icon && (
                <div
                  className={`w-8 h-8 rounded-[11px] flex items-center justify-center flex-shrink-0 ${card.color || ""}`}
                  style={card.color ? undefined : { background: card.iconBg || "var(--color-primary-100)" }}
                >
                  <Icon
                    className="w-4 h-4"
                    style={card.color ? undefined : { color: card.iconColor || "var(--color-primary-600)" }}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;